import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Bit Buddies';
const DEFAULT_DESCRIPTION = 'Discover, acquire, or invest in innovative software and systems ideas. Bit Buddies is your marketplace for blockchain, AI, and cutting-edge technology concepts.';
const DEFAULT_IMAGE = '/og-image.png';
const SITE_URL = 'https://bitbuddies.online';

const SEO = ({ 
  title, 
  description = DEFAULT_DESCRIPTION, 
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  article = null,
  structuredData = null 
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Innovative Software Ideas Marketplace`;
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="software ideas, blockchain, AI, machine learning, tech investment, buy software, invest in technology, startup ideas, SaaS, enterprise software" />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Article specific (for idea pages) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:section" content={article.section} />
          {article.tags?.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Structured Data / JSON-LD */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Generate structured data for an idea/product
export const generateIdeaStructuredData = (idea) => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": idea.title,
    "description": idea.description,
    "category": idea.category,
    "offers": idea.buy_price ? {
      "@type": "Offer",
      "price": idea.buy_price,
      "priceCurrency": "USD",
      "availability": idea.status === 'available' ? "https://schema.org/InStock" : "https://schema.org/SoldOut"
    } : undefined,
    "brand": {
      "@type": "Brand",
      "name": "Bit Buddies"
    }
  };
};

// Generate organization structured data
export const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Bit Buddies",
  "url": "https://bitbuddies.online",
  "logo": "https://bitbuddies.online/logo.png",
  "description": "Marketplace for innovative software and systems ideas",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Addison",
    "addressRegion": "TX",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "MDH@BitBuddies.online",
    "contactType": "sales"
  }
};

// Generate website structured data
export const websiteStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Bit Buddies",
  "url": "https://bitbuddies.online",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://bitbuddies.online/ideas?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

export default SEO;
